import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Typography,
  Stack,
  Tooltip,
  Checkbox,
  Icon,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

// Icon mapping for common expense categories
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HotelIcon from '@mui/icons-material/Hotel';
import FlightIcon from '@mui/icons-material/Flight';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

/**
 * NestedExpandable: Base MUI table for line-item data entry with add/delete row actions.
 *
 * Props
 * - field: {
 *     name: string,
 *     helperText?: string,
 *     tables: Array<{
 *       name: string,
 *       label?: string,
 *       selectable?: boolean, // optional compatibility
 *       fields: Array<{ name: string, label: string, type?: 'text'|'number'|'date', width?: number }>
 *     }>
 *   }
 * - formData: object (global form state)
 * - setFormData: function(prev => next) to update state
 */
export default function NestedExpandable(props) {
  const rootKey = props.field?.name;
  const tables = Array.isArray(props.field?.tables) ? props.field.tables : [];

  // Ensure nested structure exists: formData[rootKey][tableName] -> []
  const ensureTableArray = (prev, tableName) => {
    const next = { ...prev };
    if (!next[rootKey]) next[rootKey] = {};
    if (!Array.isArray(next[rootKey][tableName])) next[rootKey][tableName] = [];
    return next;
  };

  const buildEmptyRow = (cols) => {
    const row = {};
    (cols || []).forEach((c) => {
      switch (c.type) {
        case 'number':
          row[c.name] = '';
          break;
        case 'date':
          row[c.name] = null; // Dayjs later
          break;
        default:
          row[c.name] = '';
      }
    });
    return row;
  };

  const updateCell = (tableName, rowIndex, colName, value) => {
    props.setFormData((prev) => {
      const next = ensureTableArray(prev, tableName);
      const rows = [...next[rootKey][tableName]];
      const row = { ...(rows[rowIndex] || {}) };
      row[colName] = value;
      rows[rowIndex] = row;
      next[rootKey] = { ...next[rootKey], [tableName]: rows };
      return { ...next };
    });
  };

  const addRow = (tableName, cols) => {
    props.setFormData((prev) => {
      const next = ensureTableArray(prev, tableName);
      const rows = [...next[rootKey][tableName]];
      rows.push(buildEmptyRow(cols));
      next[rootKey] = { ...next[rootKey], [tableName]: rows };
      return { ...next };
    });
  };

  const deleteRow = (tableName, rowIndex) => {
    props.setFormData((prev) => {
      const next = ensureTableArray(prev, tableName);
      const rows = [...next[rootKey][tableName]];
      rows.splice(rowIndex, 1);
      next[rootKey] = { ...next[rootKey], [tableName]: rows };
      // Also trim optional selection array if it exists for compatibility
      const selKey = `${tableName}_selected`;
      if (Array.isArray(next[rootKey][selKey])) {
        const s = [...next[rootKey][selKey]];
        s.splice(rowIndex, 1);
        next[rootKey][selKey] = s;
      }
      return { ...next };
    });
  };

  const toggleSelect = (tableName, rowIndex) => {
    const selKey = `${tableName}_selected`;
    props.setFormData((prev) => {
      const next = ensureTableArray(prev, tableName);
      const exists = Array.isArray(next[rootKey][selKey])
        ? [...next[rootKey][selKey]]
        : [];
      exists[rowIndex] = !exists[rowIndex];
      next[rootKey] = { ...next[rootKey], [selKey]: exists };
      return { ...next };
    });
  };

  const getIconForExpense = (expenseName) => {
    const name = (expenseName || '').toLowerCase();
    if (name.includes('car') || name.includes('rental') || name.includes('auto')) {
      return <DirectionsCarIcon fontSize="small" color="action" />;
    }
    if (name.includes('hotel') || name.includes('lodging')) {
      return <HotelIcon fontSize="small" color="action" />;
    }
    if (name.includes('flight') || name.includes('air') || name.includes('airline')) {
      return <FlightIcon fontSize="small" color="action" />;
    }
    if (name.includes('diem') || name.includes('food') || name.includes('meal')) {
      return <RestaurantIcon fontSize="small" color="action" />;
    }
    return <MoreHorizIcon fontSize="small" color="action" />;
  };

  const renderEditor = (tableName, rowIndex, col, value) => {
    const isReadOnly = Boolean(col.readonly);
    const textAlign = col.align || 'left';
    
    const commonProps = {
      fullWidth: true,
      size: 'small',
      value: value ?? '',
      onChange: (e) => updateCell(tableName, rowIndex, col.name, e.target.value),
      placeholder: col.placeholder,
      InputProps: isReadOnly ? { readOnly: true } : undefined,
      sx: {
        '& input': { textAlign },
        '& .MuiInputBase-input': { textAlign },
      },
    };

    switch (col.type) {
      case 'number':
        return <TextField type="number" variant="outlined" {...commonProps} />;
      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={value || null}
              onChange={(newVal) => updateCell(tableName, rowIndex, col.name, newVal)}
              disabled={isReadOnly}
              slotProps={{ textField: { variant: 'outlined', fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>
        );
      default:
        return <TextField variant="outlined" {...commonProps} />;
    }
  };

  if (!rootKey || !tables.length) return null;

  return (
    <Box>
      {tables.map((table, tIdx) => {
        const tableName = table.name;
        const rows = props.formData?.[rootKey]?.[tableName] || [];
        const selectable = Boolean(table.selectable);

  // Keep checkbox and actions narrow; allow data columns to auto-fill remaining width

        return (
          <TableContainer
            component={Paper}
            elevation={0}
            key={tIdx}
            sx={{
              mb: 1,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              // Match Project_Table container border on all sides
              border: (theme) => `1px solid ${theme.palette.divider}`,
              overflowX: 'hidden',
              // Remove cell borders so we can draw only horizontal row lines like Project_Table
              '& th, & td': { border: 0 },
              '& table': { width: '100%' },
            }}
          >
            <Box p={1} pb={0}>
              {(table.label || props.field.label) && (
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {table.label || props.field.label}
                </Typography>
              )}
            </Box>

            <Table
              aria-label={`${tableName}-table`}
              sx={{
                width: '100%',
                tableLayout: 'fixed',
                borderLeft: 'none',
                borderRight: 'none',
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                borderSpacing: 0,
              }}
            >
              <TableHead>
                <TableRow sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                  {selectable && <TableCell padding="checkbox" sx={{ width: 40 }} />}
                  {table.fields.map((c) => (
                    <TableCell key={c.name} sx={{ width: c.width || 'auto' }}>{c.label}</TableCell>
                  ))}
                  <TableCell sx={{ width: 56, whiteSpace: 'nowrap', p: 0.5, textAlign: 'center' }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Ensure the last row bottom border matches the light divider color */}
                
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={(selectable ? 1 : 0) + table.fields.length + 1}
                      align="center"
                      sx={{ py: 3, color: 'text.secondary' }}
                    >
                      No rows yet. Use "Add Row" to start.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, rIdx) => (
                    <TableRow
                      key={rIdx}
                      sx={{
                        borderTop: (theme) => (rIdx === 0 ? 'none' : `1px solid ${theme.palette.divider}`),
                        // No bottom border on last row; rely on table's own bottom border
                        borderBottom: 'none',
                      }}
                    >
                      {selectable && (
                        <TableCell padding="checkbox" sx={{ width: 40 }}>
                          <Checkbox
                            size="small"
                            checked={Boolean(
                              formData?.[rootKey]?.[`${tableName}_selected`]?.[rIdx]
                            )}
                            onChange={() => toggleSelect(tableName, rIdx)}
                          />
                        </TableCell>
                      )}
                      {table.fields.map((col) => {
                        const cellValue = row?.[col.name];
                        const showIcon = col.icon !== false && col.name === 'expense' && tableName === 'items';
                        
                        return (
                          <TableCell key={col.name} sx={{ verticalAlign: 'top', width: col.width || 'auto', p: 1 }}>
                            {showIcon ? (
                              <Stack direction="row" spacing={1} alignItems="center">
                                {getIconForExpense(cellValue)}
                                {renderEditor(tableName, rIdx, col, cellValue)}
                              </Stack>
                            ) : (
                              renderEditor(tableName, rIdx, col, cellValue)
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap', width: 56, p: 0.5 }}>
                        <Tooltip title="Delete row">
                          <IconButton
                            color="error"
                            size="small"
                            aria-label="Delete row"
                            onClick={() => deleteRow(tableName, rIdx)}
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                            sx={{}}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <Stack direction="row" alignItems="center" justifyContent="space-between" p={2} pt={1}>
              {props.field.helperText && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', maxWidth: '70%' }}>
                  {props.field.helperText}
                </Typography>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => addRow(tableName, table.fields)}
                sx={{ boxShadow: 0 }}
              >
                Add Row
              </Button>
            </Stack>
          </TableContainer>
        );
      })}
    </Box>
  );
}
